import { formatUnits, parseUnits } from '@ethersproject/units';
import React, { useMemo, useState } from 'react';
import { useContext } from 'react';
import {
    DEPOSIT_PENDING,
    APPROVAL_PENDING,
    INSUFFICIENT_FUNDS_ERROR,
    SupportedAssets,
    SupportedChains,
} from '../../config/constants';
import modalContext from '../../context/modal/modalContext';
import TextInput from '../../components/TextInput';
import CustomModal from '../../components/CustomModal';
import Button from '../../components/Button';
import appContext from '../../context/app/appContext';
import errorContext from '../../context/error/errorContext';
import { ArrowDownIcon, AssetIcons } from '../../assets/icons';
import { Contract } from 'ethers';
import abis from '../../config/abis';
import { useWeb3React } from '@web3-react/core';
import useUserBalance from '../../hooks/web3/useUserBalance';
import { formatAccount, formatUSD } from '../../helpers/formats';
import { AddressZero, MaxUint256 } from '@ethersproject/constants';
import ASSETS from '../../config/constants/assets.json';
import ARBITRUM_ASSETS from '../../config/constants/arbitrumAssets.json';
import { calculateMargin, preventOverflow, sleep, toBigNumber } from '../../web3/utils';
import useIsApproved from '../../hooks/web3/useIsApproved';
import useUserSpools from '../../hooks/web3/vault/useUserSpools';
import CustomModalHeader from '../../components/CustomModal/CustomModalHeader';

const SpoolDeposit: React.FC = () => {
    const { account, library, chainId } = useWeb3React();
    const { sdk, selectedSpool, chainConfig } = useContext(appContext);
    const { setError } = useContext(errorContext);
    const { openModal, closeModal, setModalData } = useContext(modalContext);

    const { updateUserSpool } = useUserSpools();

    const initialDepositAddress = useMemo(
        () => ({
            from: '',
            to: '',
        }),
        []
    );

    const [depositAddresses, setDepositAddresses] = useState(initialDepositAddress);

    const userBalance = useUserBalance(selectedSpool?.asset.id);

    const { isApproved, mutate } = useIsApproved(
        selectedSpool?.asset.id,
        chainConfig.controller,
        depositAddresses.from
    );

    const onClose = () => {
        closeModal('spoolDeposit');
        setDepositAddresses(initialDepositAddress);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setDepositAddresses((prev) => ({
            ...prev,
            [name]: preventOverflow(value, selectedSpool?.asset.decimals),
        }));
    };

    const handleMaxInput = () => {
        if (!userBalance) return;

        setDepositAddresses((prev) => ({
            ...prev,
            from: formatUnits(userBalance, selectedSpool?.asset.decimals),
        }));
    };

    const approve = async () => {
        if (!selectedSpool) return;

        try {
            setModalData((prev) => ({ ...prev, message: APPROVAL_PENDING }));
            openModal('waitingModal');

            const contract = new Contract(
                selectedSpool?.asset.id,
                abis.erc20,
                library.getSigner()
            );

            let useExact = false;

            const estimatedGas = await contract.estimateGas
                .approve(chainConfig.controller, MaxUint256)
                .catch(() => {
                    // general fallback for tokens who restrict approval amounts
                    useExact = true;

                    return contract.estimateGas.approve(
                        chainConfig.controller,
                        parseUnits(depositAddresses.from, selectedSpool?.asset.decimals)
                    );
                });

            const approve = await contract.approve(
                chainConfig.controller,
                useExact
                    ? parseUnits(depositAddresses.from, selectedSpool?.asset.decimals)
                    : MaxUint256,
                {
                    gasLimit: calculateMargin(estimatedGas),
                }
            );

            await approve.wait();

            await mutate();
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    const deposit = async () => {
        if (!sdk || !selectedSpool || !userBalance) return;

        if (
            userBalance.lt(
                toBigNumber(depositAddresses.from || '0', selectedSpool?.asset.decimals)
            )
        ) {
            openModal('errorModal');
            setModalData((prev) => ({
                ...prev,
                message: INSUFFICIENT_FUNDS_ERROR,
            }));
            return;
        }

        try {
            setModalData((prev) => ({ ...prev, message: DEPOSIT_PENDING }));
            openModal('waitingModal');

            const deposit = await sdk.spools.deposit(
                selectedSpool,
                depositAddresses.from
            );

            const receipt = await deposit.wait();

            // delay so that subgraph gets updated
            await sleep(5000);

            await updateUserSpool(selectedSpool, 'pendingDepositAmount');

            onClose();
            setModalData((prev) => ({
                ...prev,
                txHash: receipt.transactionHash,
            }));
            openModal('depositPending');
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    return (
        <CustomModal modalName='spoolDeposit' className='spool-deposit'>
            <CustomModalHeader title='Deposit' onClose={onClose} />
            <div className='note deposit'>
                Your Deposit will be set to Pending, and will be deposited into the Spool
                the next time Do Hard Work is executed. (Generally within{' '}
                {chainId === SupportedChains.ARBITRUM_ONE ? '24 hours' : '3 days'})
            </div>
            <div className='content'>
                <div className='deposit-container deposit-from'>
                    <div className='field-container from'>
                        <span className='field'>From</span>
                        <span>{formatAccount(account || AddressZero)}</span>
                    </div>
                    <div className='field-container balance'>
                        <span className='field'>Balance in Wallet</span>
                        <span>
                            {userBalance
                                ? formatUSD(
                                      formatUnits(
                                          userBalance,
                                          selectedSpool?.asset.decimals
                                      ) || 0,
                                      4
                                  )
                                : '0.00'}
                        </span>
                    </div>
                    <div className='field-container input'>
                        <span className='field'>Input</span>
                        <div className='input-container'>
                            <TextInput
                                onChange={handleChange}
                                value={depositAddresses.from}
                                name='from'
                                placeholder='0.00'
                            />
                            <Button variant='secondary' onClick={handleMaxInput}>
                                MAX
                            </Button>
                        </div>
                    </div>

                    <div className='field-container token'>
                        <div>
                            {
                                AssetIcons[
                                    selectedSpool?.asset
                                        ? (chainId === SupportedChains.MAINNET
                                              ? (ASSETS as Record<string, string>)
                                              : (ARBITRUM_ASSETS as Record<
                                                    string,
                                                    string
                                                >))[selectedSpool?.asset.id]
                                        : SupportedAssets.FALLBACK
                                ]
                            }
                            <span>{selectedSpool?.asset.symbol || 'Asset'}</span>
                        </div>
                    </div>
                </div>
                <div className='arrow-swap-container'>
                    <ArrowDownIcon />
                </div>

                <div className='deposit-container deposit-to'>
                    <div className='field-container to'>
                        <span className='field'>To</span>
                        <span>{selectedSpool?.name || 'Spool'}</span>
                    </div>
                    <div className='field-container balance'>
                        <span className='field'>Balance in $SPOOL</span>
                        <span>
                            {formatUSD(selectedSpool?.userData?.userCurrentBalance || 0)}
                        </span>
                    </div>
                    <div className='field-container input'>
                        <span className='field'>Output</span>
                        <div className='input-container'>
                            <TextInput
                                value={depositAddresses.from}
                                name='to'
                                placeholder='0.00'
                                readOnly
                            />
                        </div>
                    </div>
                    <div className='field-container token'>
                        <div>
                            {
                                AssetIcons[
                                    selectedSpool?.asset
                                        ? (chainId === SupportedChains.MAINNET
                                              ? (ASSETS as Record<string, string>)
                                              : (ARBITRUM_ASSETS as Record<
                                                    string,
                                                    string
                                                >))[selectedSpool?.asset.id]
                                        : SupportedAssets.FALLBACK
                                ]
                            }
                            <span>{selectedSpool?.asset.symbol || 'Asset'}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className={`buttons-container ${isApproved ? 'single' : ''}`}>
                <Button
                    disabled={
                        !depositAddresses.from ||
                        parseUnits(
                            depositAddresses.from,
                            selectedSpool?.asset.decimals
                        ).isZero()
                    }
                    onClick={approve}
                >
                    Approve {selectedSpool?.asset.symbol || 'Asset'}
                </Button>
                <Button
                    disabled={
                        !isApproved ||
                        !depositAddresses.from ||
                        parseUnits(
                            depositAddresses.from,
                            selectedSpool?.asset.decimals
                        ).isZero()
                    }
                    onClick={deposit}
                >
                    Deposit
                </Button>
            </div>
        </CustomModal>
    );
};

export default SpoolDeposit;
